import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { useMemo } from 'react';
import { useCollectionListAll } from '../../api';
import type { ICollection, ISecodaEntity } from '../../api/index.ts';

interface OptimizedCollectionRenderProps<T> {
	record: T;
	hideOnEmpty?: boolean;
}

/**
 * Lightweight read-only component to display collections.
 * Used when user doesn't have permission to edit.
 * Uses MultiSelectorTarget for consistent collection display.
 */
export function OptimizedCollectionRender<T extends ISecodaEntity>({
	record,
	hideOnEmpty = true,
}: OptimizedCollectionRenderProps<T>) {
	const { collections } = record;
	const { data: allCollections } = useCollectionListAll();

	const selectedCollections = useMemo(() => {
		if (!collections || collections.length === 0 || !allCollections?.results) {
			return [];
		}

		const collectionIds = collections.map((c) =>
			typeof c === 'object' ? (c as ICollection).id : c
		);

		return allCollections.results
			.filter((col: ICollection) => collectionIds.includes(col.id))
			.map((col: ICollection) => ({
				label: col.title || 'Untitled',
				value: col.id,
				icon: col.icon,
				id: col.id,
			})) as SelectablePropertyItem[];
	}, [collections, allCollections]);

	return (
		<MultiSelectorTarget
			selected={selectedCollections}
			placeholder="No collections"
			hideOnEmpty={hideOnEmpty}
			readOnly
			iconType="emoji"
			property="collections"
			isMenuItemBadge={false}
			variant="tertiary"
		/>
	);
}
